import { formatDateTimeSinceNow } from "@/lib/Formatters/formatDateTimeSinceNow";
import { useTranslations } from "use-intl";
import { useState } from "react";
import { invoiceStatusIntentMap } from "../Utils/invoiceStatusIntentMap";
import { SidebarList } from "@/lib/Components/SideBarList/SideBarList";
import { useInfiniteGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  InvoiceListItem,
  invoiceListQuery,
} from "@/app/Invoices/GraphQL/invoiceListQuery";
import { getInvoiceListQueryVariables } from "@/app/Invoices/Utils/getInvoiceListQueryVariables";
import { keepPreviousData } from "@tanstack/react-query";
import { Badge } from "@/components/catalyst/badge";
import { useThrottle } from "@/lib/Utils/useThrottle";
import { Highlighter } from "@/lib/Components/Highlighter";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";

export function InvoiceListScreen() {
  const [search, setSearch] = useState("");
  const throttleSearch = useThrottle(search, 500);
  const { supplierId } = useGlobalSupplier();

  const { data, fetchNextPage, isFetching, isFetchingNextPage } =
    useInfiniteGqlQuery(
      invoiceListQuery,
      getInvoiceListQueryVariables({}, supplierId, throttleSearch),
      {
        placeholderData: keepPreviousData,
        initialPageParam: {
          page: 1,
        },
        getNextPageParam: (lastPage) => {
          return lastPage.invoices.paginatorInfo.hasMorePages
            ? {
                page: lastPage.invoices.paginatorInfo.currentPage + 1,
              }
            : undefined;
        },
      },
    );

  const items = data?.pages.flatMap((p) => p.invoices.data);
  const totalCount = data?.pages.at(0)?.invoices.paginatorInfo.total ?? 0;

  return (
    <SidebarList<InvoiceListItem>
      items={items}
      totalCount={totalCount}
      linkProps={(item) => ({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/invoices/$id",
        params: (prev) => ({
          ...prev,
          id: item.id,
        }),
      })}
      search={search}
      onSearchChange={(newStr) => {
        setSearch(newStr);
      }}
      onClearFilters={() => {
        setSearch("");
      }}
      listRoute={"/$tenant/$orgId/supplier/{-$supplierId}/invoices"}
      createRoute={"/$tenant/$orgId/supplier/{-$supplierId}/invoices/create"}
      fetchNextPage={fetchNextPage}
      isFetchingNextPage={isFetchingNextPage}
      isFetching={isFetching}
      itemNode={(item) => <Node model={item} search={search} />}
    />
  );
}

function Node({ model, search }: { model: InvoiceListItem; search: string }) {
  const t = useTranslations("invoice");

  return (
    <div className="relative flex flex-row items-start justify-between gap-3">
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="text-sm font-medium text-gray-900">
          <Highlighter searchString={search}>{model.reference}</Highlighter>
        </p>
        <p className="text-sm text-gray-500">
          <Highlighter searchString={search}>
            {model.payee.name ?? "--"}
          </Highlighter>
        </p>
        <p className="mt-1 text-xs text-gray-600">
          {formatCurrency(model.amount_inc_tax, model.currency)}
        </p>
        <p className="mt-1 text-xs italic text-gray-500">
          {formatDateTimeSinceNow(model.created_at)}
        </p>
      </div>
      <div className="absolute right-0 top-0 flex-shrink-0">
        <Badge color={invoiceStatusIntentMap[model.status]}>
          {t(`status.${model.status}`)}
        </Badge>
      </div>
    </div>
  );
}
